import { useEffect, useMemo, useState } from 'react';

import {
  getChangePlanPreviewRequest,
  type ChangePlanPreviewResponse,
} from 'services/subscriptions';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  UserSubscription,
} from 'types';

export type CheckoutProration = {
  creditAmount: number;
  totalDueToday: number;
  planLinePrice: number;
  currency?: string;
  renewalDate?: string | null;
  fromPlanName?: string;
};

type UseCheckoutProrationParams = {
  displayPayButton: boolean;
  checkoutPlanId: string | undefined;
  activeSubscription: UserSubscription | null | undefined;
  frequency: string | null;
  currentPrice: number;
};

const roundMoney = (value: number) => Math.round(value * 100) / 100;

const isPaidActiveSubscription = (
  active: UserSubscription | null | undefined,
): boolean => {
  if (!active?.id) return false;
  if (active.status === SubscriptionStatus.CANCELLED) return false;
  if (active.planId === SubscriptionStatus.TRIAL) return false;
  return true;
};

const planPriceFromPreview = (
  newPlan: ChangePlanPreviewResponse['newPlan'],
  frequency: SubscriptionFrequency,
  fallback: number,
): number => {
  if (!newPlan) return fallback;
  const yearly = newPlan.yearlyValue;
  const monthly = newPlan.monthlyValue;
  if (frequency === SubscriptionFrequency.YEAR && yearly != null) {
    return typeof yearly === 'number' ? yearly : fallback;
  }
  if (frequency === SubscriptionFrequency.MONTH && monthly != null) {
    return typeof monthly === 'number' ? monthly : fallback;
  }
  return fallback;
};

const mapPreviewToProration = (
  data: ChangePlanPreviewResponse,
  frequency: SubscriptionFrequency,
  fallbackLinePrice: number,
): CheckoutProration => {
  const planLinePrice = planPriceFromPreview(
    data.newPlan,
    frequency,
    fallbackLinePrice,
  );
  const totalDueToday = Math.max(0, data.amountDue);
  const creditAmount =
    data.creditAmount != null
      ? data.creditAmount
      : roundMoney(Math.max(0, planLinePrice - totalDueToday));

  const fromPlanName =
    data.currentPlan?.productName || data.currentPlan?.name || undefined;

  return {
    creditAmount,
    totalDueToday,
    planLinePrice:
      data.newPlanFullPrice != null ? data.newPlanFullPrice : planLinePrice,
    currency: data.currency,
    renewalDate: data.renewalDate ?? null,
    fromPlanName,
  };
};

export const useCheckoutProration = ({
  displayPayButton,
  checkoutPlanId,
  activeSubscription,
  frequency,
  currentPrice,
}: UseCheckoutProrationParams) => {
  const [proration, setProration] = useState<CheckoutProration | null>(null);

  const isChangePlanCheckout = useMemo(() => {
    if (!displayPayButton || !checkoutPlanId || !activeSubscription) {
      return false;
    }
    if (!isPaidActiveSubscription(activeSubscription)) {
      return false;
    }

    const currentPlanId =
      activeSubscription.subscriptionPlanId ??
      activeSubscription.subscriptionPlan?.id;
    return Boolean(currentPlanId && currentPlanId !== checkoutPlanId);
  }, [displayPayButton, checkoutPlanId, activeSubscription]);

  const fromPlanName = useMemo(() => {
    if (proration?.fromPlanName) {
      return proration.fromPlanName;
    }
    const plan = activeSubscription?.subscriptionPlan;
    return plan?.productName || plan?.name || 'Pro';
  }, [activeSubscription?.subscriptionPlan, proration?.fromPlanName]);

  useEffect(() => {
    if (!isChangePlanCheckout || !checkoutPlanId) {
      setProration(null);
      return;
    }

    const freq =
      frequency === SubscriptionFrequency.YEAR ||
      frequency === SubscriptionFrequency.MONTH
        ? frequency
        : null;
    if (!freq) {
      setProration(null);
      return;
    }

    let cancelled = false;
    setProration(null);

    void (async () => {
      try {
        const data = await getChangePlanPreviewRequest({
          subscriptionPlanId: checkoutPlanId,
          frequency: freq,
        });
        if (cancelled) return;
        setProration(mapPreviewToProration(data, freq, currentPrice));
      } catch {
        if (!cancelled) {
          setProration(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isChangePlanCheckout, checkoutPlanId, frequency, currentPrice]);

  const pricesReady = !isChangePlanCheckout || proration !== null;

  const showProrationCredit =
    isChangePlanCheckout && proration != null && proration.creditAmount > 0;

  return {
    isUpgradeCheckout: isChangePlanCheckout,
    proration,
    pricesReady,
    fromPlanName,
    showProrationCredit,
    renewalDateFromPreview: proration?.renewalDate ?? null,
  };
};
